import { parseRoute, projectPath, type Route } from "./routes.ts";

/**
 * The Project the user was last in, so Settings / Usage / Home / Pair can offer "Back to project" without forcing a
 * trip through Home. Session-scoped (a new tab starts clean); validated on read, so a tampered or stale value can
 * never produce anything but a normal Project route.
 */
export interface ProjectContext {
  machineId: string;
  projectId: string;
  chatId?: string | undefined;
}

const KEY = "racler.lastProject.v1";
const LEGACY_KEY = "racler.lastWorkspace.v1";
type Store = Pick<Storage, "getItem" | "setItem" | "removeItem">;

const defaultStore = (): Store | undefined => {
  try {
    return sessionStorage;
  } catch {
    return undefined;
  }
};

export function contextFromRoute(route: Route): ProjectContext | null {
  if (route.name !== "project" || !route.projectId) return null;
  return {
    machineId: route.machineId,
    projectId: route.projectId,
    ...(route.chatId ? { chatId: route.chatId } : {}),
  };
}

export function rememberProject(ctx: ProjectContext, store: Store | undefined = defaultStore()): void {
  try {
    store?.setItem(KEY, projectPath(ctx.machineId, ctx.projectId, ctx.chatId));
    store?.removeItem(LEGACY_KEY);
  } catch {
    // storage unavailable: "Back to project" simply is not offered
  }
}

export function forgetProject(store: Store | undefined = defaultStore()): void {
  try {
    store?.removeItem(KEY);
    store?.removeItem(LEGACY_KEY);
  } catch {
    // ignore
  }
}

function fromUrl(raw: string | null | undefined): ProjectContext | null {
  if (!raw || !raw.startsWith("/m/")) return null;
  const [pathname = "", search = ""] = raw.split("?");
  return contextFromRoute(parseRoute(pathname, search ? `?${search}` : ""));
}

/**
 * The remembered Project as a context, only if the stored URL parses back into a valid Project route. A value saved by
 * a pre-M2C.1 build (old /w/ URL, old key) is still understood; its ids were kept by the migration.
 */
export function lastProject(store: Store | undefined = defaultStore()): ProjectContext | null {
  try {
    return fromUrl(store?.getItem(KEY)) ?? fromUrl(store?.getItem(LEGACY_KEY));
  } catch {
    return null;
  }
}

export const sameProject = (a: ProjectContext | null, b: ProjectContext | null): boolean =>
  !!a && !!b && a.machineId === b.machineId && a.projectId === b.projectId;

/**
 * Leaving the Project page unmounts its editor, which would silently drop unsaved edits. The guard lets a navigation
 * through only if nothing would be lost or the user confirms. Staying in the same Project (another chat, same route)
 * never prompts.
 */
export function shouldConfirmLeave(
  from: ProjectContext | null,
  targetPath: string,
  dirtyCount: number,
): boolean {
  if (dirtyCount === 0 || !from) return false;
  const [pathname = "", search = ""] = targetPath.split("?");
  const route = parseRoute(pathname, search ? `?${search}` : "");
  return !sameProject(from, contextFromRoute(route));
}

/** Where "Back to ..." should lead; null when the user never opened a Project in this tab. */
export const backTarget = (store?: Store): string | null => {
  const c = lastProject(store);
  return c ? projectPath(c.machineId, c.projectId, c.chatId) : null;
};
